import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FavoriteOptionsData } from "@nestcoinco/hyper-api-gateway-api-client";

import { updateGameFavoriteStatus } from "../../api-clients/gamesFavoritesClient";
import { showSuccessToast } from "../../utils/show-toast";
import { QUERY_KEYS } from "../constants";
import { useCallback } from "react";

interface UseFavoriteGameMutationFnParams {
  gameId: string;
  isFavorite: boolean;
}

const QUERIES_TO_INVALIDATE_ON_GAME_LIKE = [
  QUERY_KEYS.GAME.ALL_GAMES,
  QUERY_KEYS.GAME.POPULAR_GAMES,
  QUERY_KEYS.GAME.MY_FAVORITE_GAMES,
  QUERY_KEYS.GAME.GAME_DETAILS,
];

function useFavoriteGameMutation() {
  const queryClient = useQueryClient();

  const handleSuccessfulMutation = (data: FavoriteOptionsData) => {
    const toastMessage = data.isFavorite
      ? "Added to favorites"
      : "Removed from favorites";
    showSuccessToast(toastMessage);
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          QUERIES_TO_INVALIDATE_ON_GAME_LIKE.includes(key[0])
        );
      },
    });
  };

  const mutation = useMutation({
    mutationFn: ({ gameId, isFavorite }: UseFavoriteGameMutationFnParams) =>
      updateGameFavoriteStatus(gameId, isFavorite),
    onSuccess: handleSuccessfulMutation,
  });

  const addGameToFavorites = useCallback(
    (gameId: string) => {
      if (!gameId) return;
      mutation.mutate({ gameId, isFavorite: true });
    },
    [mutation]
  );

  const removeGameFromFavorites = useCallback(
    (gameId: string) => {
      if (!gameId) return;
      mutation.mutate({ gameId, isFavorite: false });
    },
    [mutation]
  );

  return { mutation, addGameToFavorites, removeGameFromFavorites };
}
export default useFavoriteGameMutation;
